import { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, addDays, isAfter, differenceInDays, isBefore, subDays, isEqual } from "date-fns";
import { useWeek } from "../../contexts/WeekContext";
import { Icon } from "@iconify/react/dist/iconify.js";

const WeekPicker = () => {
  const { dateRange, setDateRange, resetToDefaultRange, isCurrentRangeDefault } = useWeek();
  const [warning, setWarning] = useState("");
  const [startDate, setStartDate] = useState(dateRange.start);
  const [endDate, setEndDate] = useState(dateRange.end);
  
  // Calculate the minimum selectable date (30 days ago from today)
  const today = new Date();
  // Set hours to beginning of day to avoid time comparison issues
  today.setHours(0, 0, 0, 0);
  const minDate = subDays(today, 29); // 30 days including today
  minDate.setHours(0, 0, 0, 0);
  
  // Sync local state with context when dateRange changes
  useEffect(() => {
    setStartDate(dateRange.start);
    setEndDate(dateRange.end);
  }, [dateRange.start, dateRange.end]);


  
  // Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleDateChange = ([start, end]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // When only start date is selected (end is null)
    if (start && !end) {
      // Create a copy of start date with time set to beginning of day
      const startCopy = new Date(start);
      startCopy.setHours(0, 0, 0, 0);
      
      // Validate start date isn't before minDate
      if (startCopy < minDate) {
        setWarning("⚠️ Cannot select dates more than 30 days in the past.");
        return;
      }

      // If start date is valid, update it
      setWarning("");
      setStartDate(start);
      setEndDate(null);
      return; // Exit early, don't update dateRange until end date is selected
    }
    
    // When both start and end dates are selected
    if (start && end) {
      // Create copies with time set to beginning of day
      const startCopy = new Date(start);
      startCopy.setHours(0, 0, 0, 0);
      const endCopy = new Date(end);
      endCopy.setHours(0, 0, 0, 0);
      
      // Validate end date isn't in the future
      if (endCopy > today) {
        setWarning("⚠️ Cannot select dates beyond today.");
        return;
      }
      
      // Validate range isn't more than 30 days
      if (differenceInDays(end, start) > 29) {
        setWarning("⚠️ Date range cannot exceed 30 days.");
        return;
      }
      
      // Validate start date isn't before minDate
      if (startCopy < minDate) {
        setWarning("⚠️ Cannot select dates more than 30 days in the past.");
        return;
      }

      setWarning("");
      setStartDate(start);
      setEndDate(end);
      setDateRange({ start, end });
    }
  };

  const handleReset = () => {
    resetToDefaultRange();
    setWarning("");
  };

  const formattedRange = () => {
    if (!dateRange.start || !dateRange.end) return "Select date range";
    return `${format(dateRange.start, "MMM d, yyyy")} – ${format(dateRange.end, "MMM d, yyyy")}`;
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-center gap-2">
        <DatePicker
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          dateFormat="MM/dd/yyyy"
          placeholderText="Select date range"
          calendarClassName="rounded-md"
          minDate={minDate}
          maxDate={today}
          dayClassName={(date) => {
            // Custom day class logic
            if (startDate && !endDate && isSameDay(date, startDate)) {
              return "bg-primary text-font-primary rounded-full"; // Style for start date when no end date is selected yet
            }
            if (startDate && endDate && 
                (isSameDay(date, startDate) || isSameDay(date, endDate))) {
              return "bg-primary text-font-primary rounded-full"; // Style for start and end dates
            }
            if (startDate && endDate && date > startDate && date < endDate) {
              return "text-primary"; // Style for dates in between
            }
            return undefined;
          }}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="flex gap-2 items-center px-4 py-2 justify-center">
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                className="text-xl text-accent"
              >
                <Icon icon="mingcute:left-line" className=""></Icon>
              </button>
              <span className="text-base text-font-primary">
                {format(date, "MMMM yyyy")}
              </span>
              <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                className="text-xl text-accent"
              >
                <Icon icon="mingcute:right-line" className=""></Icon>
              </button>
            </div>
          )}
          customInput={
            <button className="flex flex-row gap-1 items-center button-container py-2 flex-1 px-3 rounded-md text-font-primary h-full hover:shadow-none transition-colors duration-300 ease-in-out">
              <Icon icon="stash:data-date" className="text-2xl text-accent" />
              {formattedRange()}
            </button>
          }
        />
        
        {/* Reset button - only show when not using default range */}
        {!isCurrentRangeDefault() && (
          <button 
            onClick={handleReset}
            className="flex items-center justify-center h-full p-2 button-container rounded-md text-accent hover:shadow-none transition-colors duration-300 ease-in-out"
            title="Reset to default range"
          >
            <Icon icon="material-symbols:refresh" className="text-xl" />
          </button>
        )}
      </div>
      {warning && <p className="text-sm">{warning}</p>}
    </div>
  );
};

export default WeekPicker;








